import React, { createContext, useContext, useEffect, useState } from 'react';

const AffiliateContext = createContext();

export const useAffiliate = () => {
  const context = useContext(AffiliateContext);
  if (!context) {
    throw new Error('useAffiliate must be used within an AffiliateProvider');
  }
  return context;
};

export const AffiliateProvider = ({ children }) => {
  const [affiliateCode, setAffiliateCode] = useState(null);

  // Cookie management utilities
  const setCookie = (name, value, days = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  };

  const getCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  const deleteCookie = (name) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  };

  // Initialize affiliate code from URL or cookie on mount
  useEffect(() => {
    const initializeAffiliate = () => {
      // Check for affiliate code in URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const urlAffiliateCode = urlParams.get('aff');

      if (urlAffiliateCode) {
        // Store affiliate code in cookie (permanent until manual deletion)
        setCookie('brandvox_affiliate', urlAffiliateCode);
        setAffiliateCode(urlAffiliateCode);

        // Clean up URL by removing the affiliate parameter
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('aff');
        window.history.replaceState({}, document.title, newUrl.toString());

      } else {
        // Check if affiliate code exists in cookie
        const cookieAffiliateCode = getCookie('brandvox_affiliate');
        if (cookieAffiliateCode) {
          setAffiliateCode(cookieAffiliateCode);
        }
      }
    };

    initializeAffiliate();
  }, []);

  // Function to manually set affiliate code
  const setAffiliate = (code) => {
    if (code && code.trim()) {
      setCookie('brandvox_affiliate', code.trim());
      setAffiliateCode(code.trim());
    }
  };

  // Function to clear affiliate code
  const clearAffiliate = () => {
    deleteCookie('brandvox_affiliate');
    setAffiliateCode(null);
  };

  // Function to get current affiliate code for checkout
  const getAffiliateForCheckout = () => {
    const currentCode = affiliateCode || getCookie('brandvox_affiliate');
    return currentCode;
  };

  // Function to track affiliate click (for analytics)
  const trackAffiliateClick = (code) => {
    try {
      // Send analytics event if tracking is available
      if (window.gtag) {
        window.gtag('event', 'affiliate_click', {
          'affiliate_code': code,
          'timestamp': new Date().toISOString()
        });
      }

    } catch (error) {
    }
  };

  const value = {
    affiliateCode,
    setAffiliate,
    clearAffiliate,
    getAffiliateForCheckout,
    trackAffiliateClick,
    hasAffiliate: !!affiliateCode
  };

  return (
    <AffiliateContext.Provider value={value}>
      {children}
    </AffiliateContext.Provider>
  );
};

export default AffiliateContext;